"use client"

import Link from "next/link"
import { useState, useMemo, useEffect, createContext, useContext } from "react"
import { usePathname } from "next/navigation"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Sheet, SheetContent } from "@/components/ui/sheet"

type SidebarItem = {
  title: string
  href: string
  isPremium: boolean
}

type SidebarItems = {
  [category: string]: SidebarItem[]
}

interface SidebarProps {
  items: SidebarItems
}

const SidebarContext = createContext<{
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}>({
  isOpen: false,
  setIsOpen: () => {},
})

export const useSidebar = () => useContext(SidebarContext)

export function SidebarForStudy({ items }: SidebarProps) {
  const [openCategories, setOpenCategories] = useState<string[]>([])
  const pathname = usePathname()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    // パスが変更されたときに開いているカテゴリをリセット
    setOpenCategories(Object.keys(items))
  }, [pathname, items])

  const toggleCategory = (category: string) => {
    setOpenCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    )
  }

  const categories = useMemo(() => Object.entries(items), [items])

  const SidebarContent = () => (
    <ScrollArea className="h-full">
      <div className="py-4 px-3">
        <Link href="/study">
          <h2 className="px-4 text-2xl font-bold tracking-tight pt-2 pb-6">
            Study
          </h2>
        </Link>
        <Accordion type="multiple" value={openCategories} className="w-full">
          {categories.map(([category, links]) => (
            <AccordionItem key={category} value={category}>
              <AccordionTrigger
                onClick={() => toggleCategory(category)}
                className="py-4 px-4 text-base font-semibold"
              >
                {category}
              </AccordionTrigger>
              <AccordionContent>
                <nav className="space-y-1 px-4">
                  {links.map((link) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      className="flex justify-between items-center py-2 px-3 text-sm rounded-md hover:bg-accent hover:text-accent-foreground"
                      onClick={() => setIsOpen(false)}
                    >
                      <span>{link.title}</span>
                      {link.isPremium ? (
                        <Badge variant="premium">Premium</Badge>
                      ) : (
                        <Badge variant="free">Free</Badge>
                      )}
                    </Link>
                  ))}
                </nav>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </ScrollArea>
  )

  return (
    <SidebarContext.Provider value={{ isOpen, setIsOpen }}>
      <div className="hidden xl:block w-80 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <SidebarContent />
      </div>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent side="left" className="w-64 p-0">
          <SidebarContent />
        </SheetContent>
      </Sheet>
    </SidebarContext.Provider>
  )
}
